import { Box, Link, Typography } from "@mui/material";
import { alpha, keyframes, useTheme, type Theme } from "@mui/material/styles";
import type {
  HealthStatus,
  MonitoredServersSummary,
  PulseHeadline,
  PulseSystem,
} from "@xop/dashboard-shared";
import { Link as RouterLink } from "react-router-dom";

const dotPulse = keyframes({
  "0%, 100%": { opacity: 1 },
  "50%": { opacity: 0.5 },
});

/**
 * Compact full-width hero — greeting + mood-tinted status sentence on
 * the left, system-health pills on the right, all in a single visual
 * row that wraps cleanly on narrow screens.
 *
 * No tile grid: the headline sentence ("2 systems down (Support API,
 * CRM). 12 open tickets.") and the pills already convey every number
 * a stacked tile row used to add, with far less vertical real estate.
 */
export function PulseHeader({
  headline,
  systems,
  monitored_servers,
  generated_at,
  isRefetching,
  userFirstName,
}: {
  headline: PulseHeadline;
  systems: PulseSystem[];
  monitored_servers: MonitoredServersSummary | null;
  generated_at: string | undefined;
  isRefetching: boolean;
  userFirstName: string;
}): JSX.Element {
  const theme = useTheme();
  const tint = moodTint(theme, headline.mood);
  const healthCount = systems.filter((s) => s.status === "healthy").length;
  return (
    <Box
      component="section"
      data-testid="pulse-header"
      data-mood={headline.mood}
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 1,
        border: 1,
        boxShadow: theme.shadows[1],
        transition: "background-color 150ms, border-color 150ms",
        ...tint.surface,
      }}
    >
      {/* Decorative soft radial highlight in the upper-right gives the
       *  banner a hint of depth without bumping into the headline. */}
      <Box
        aria-hidden
        sx={{
          pointerEvents: "none",
          position: "absolute",
          inset: 0,
          opacity: 0.5,
          background: `radial-gradient(60% 100% at 90% 0%, ${theme.palette.background.paper} 0%, transparent 70%)`,
        }}
      />
      <Box sx={{ position: "relative", display: "grid", gap: 1, px: { xs: 2, sm: 2.5 }, py: { xs: 1.5, sm: 1.75 } }}>
        {/* Kicker row — live indicator + auto-updating status */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.75,
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: "text.disabled",
          }}
        >
          <Box
            aria-hidden
            sx={{
              display: "inline-block",
              height: 8,
              width: 8,
              borderRadius: "50%",
              bgcolor: tint.dot,
              animation: `${dotPulse} 2s ease-in-out infinite`,
            }}
          />
          <span>Company pulse · Live</span>
          {generated_at && <span>· updated {timeAgoShort(generated_at)}</span>}
          {isRefetching && <span>· updating…</span>}
          {systems.length > 0 && (
            <>
              <Box component="span" aria-hidden sx={{ display: { xs: "none", sm: "inline" }, color: "divider" }}>
                ·
              </Box>
              <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {healthCount}/{systems.length} systems healthy
              </Box>
            </>
          )}
          {monitored_servers && monitored_servers.total > 0 && (
            <>
              <Box component="span" aria-hidden sx={{ display: { xs: "none", sm: "inline" }, color: "divider" }}>
                ·
              </Box>
              <Link
                component={RouterLink}
                to="/server"
                data-testid="monitored-servers-chip"
                underline="hover"
                sx={{
                  fontVariantNumeric: "tabular-nums",
                  color: monitored_servers.healthy < monitored_servers.total ? "error.main" : "success.main",
                }}
              >
                {monitored_servers.healthy}/{monitored_servers.total} servers healthy
              </Link>
            </>
          )}
        </Box>

        {/* Main row — title + sentence on the left, pills on the right */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", columnGap: 2, rowGap: 1 }}>
          {/* The greeting keeps a floor of 280px. With a bare `flex: 1` it
              shrank instead of the row wrapping, so on a narrow window the
              sentence set itself one word per line beside a full-width strip
              of pills (2026-09-16). A basis forces the pills onto their own
              line instead, which is what "wrap" was there to do. */}
          <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
            <Typography
              component="h1"
              sx={{ fontSize: { xs: "1rem", sm: "1.125rem" }, fontWeight: 600, lineHeight: 1.25, letterSpacing: "-0.01em", color: "text.primary" }}
            >
              Hey {userFirstName} —{" "}
              <Box component="span" data-testid="pulse-sentence" sx={{ fontWeight: 400, color: tint.sentence }}>
                {headline.sentence.replace(/\.$/, "")}
              </Box>
            </Typography>
          </Box>
          {systems.length > 0 && (
            <Box
              component="nav"
              aria-label="System health"
              data-testid="system-health-strip"
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                // Right-aligned beside the greeting; left-aligned once they
                // have wrapped onto a line of their own.
                justifyContent: { xs: "flex-start", md: "flex-end" },
                gap: 0.75,
              }}
            >
              {systems.map((sys) => (
                <SystemPill key={sys.id} sys={sys} />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function SystemPill({ sys }: { sys: PulseSystem }): JSX.Element {
  const theme = useTheme();
  const tint = statusTint(theme, sys.status);
  return (
    <Link
      component={RouterLink}
      to="/server"
      data-status={sys.status}
      data-testid={`system-pill-${sys.id}`}
      title={`${sys.label} · ${sys.status}${sys.latency_ms != null ? ` · ${sys.latency_ms}ms` : ""}`}
      underline="none"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        borderRadius: 999,
        border: 1,
        px: 1,
        py: 0.25,
        fontSize: 11,
        transition: "all 150ms ease-out",
        "&:hover": { transform: "translateY(-1px)" },
        ...tint.surface,
      }}
    >
      <Box
        aria-hidden
        sx={{
          display: "inline-block",
          height: 6,
          width: 6,
          borderRadius: "50%",
          bgcolor: tint.dot,
          ...(sys.status === "healthy" ? { animation: `${dotPulse} 2s ease-in-out infinite` } : {}),
        }}
      />
      <Box component="span" sx={{ fontWeight: 500 }}>{sys.label}</Box>
      {sys.latency_ms != null && sys.status === "healthy" && (
        <Box component="span" sx={{ fontVariantNumeric: "tabular-nums", color: "text.disabled" }}>
          {sys.latency_ms}ms
        </Box>
      )}
    </Link>
  );
}

/** Mood tint for the hero surface (was MOOD_STYLES). */
function moodTint(
  theme: Theme,
  mood: PulseHeadline["mood"],
): { surface: object; dot: string; sentence: string } {
  switch (mood) {
    case "watch":
      return {
        surface: {
          borderColor: alpha(theme.palette.warning.main, 0.4),
          bgcolor: alpha(theme.palette.warning.main, 0.08),
        },
        dot: theme.palette.warning.main,
        sentence: theme.palette.text.primary,
      };
    case "alert":
      return {
        surface: {
          borderColor: alpha(theme.palette.error.main, 0.5),
          bgcolor: alpha(theme.palette.error.main, 0.08),
        },
        dot: theme.palette.error.main,
        sentence: theme.palette.text.primary,
      };
    case "calm":
    default:
      return {
        surface: {
          borderColor: "divider",
          backgroundImage: `linear-gradient(to bottom right, ${theme.palette.background.paper}, ${theme.palette.surfaceSoft})`,
        },
        dot: theme.palette.success.main,
        sentence: theme.palette.text.secondary,
      };
  }
}

/** Pill tint per system status (was STATUS_TINT). */
function statusTint(
  theme: Theme,
  status: HealthStatus,
): { surface: object; dot: string } {
  const make = (c: string) => ({
    surface: {
      borderColor: alpha(c, 0.35),
      bgcolor: alpha(c, 0.1),
      color: "text.primary",
      "&:hover": { bgcolor: alpha(c, 0.15) },
    },
    dot: c,
  });
  switch (status) {
    case "healthy":
      return make(theme.palette.success.main);
    case "degraded":
      return make(theme.palette.warning.main);
    case "down":
      return make(theme.palette.error.main);
    case "unknown":
    default:
      return {
        surface: {
          borderColor: "divider",
          bgcolor: "surfaceSoft",
          color: "text.secondary",
        },
        dot: theme.palette.text.disabled,
      };
  }
}

function timeAgoShort(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const sec = Math.floor(ms / 1000);
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}
